import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    GraphQLResponse,
    Pagination,
    ProtoApiService
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Date as DateModel } from '@lsq-frontend/remote-services/all-java/common-mk0';
import { BankAccountType } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardFinancingSellerClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    InvoiceSummary2,
    Money,
    Payable,
    PayablesInput,
    PayablesResponse,
    Query,
    SortDirection,
    TransferType
} from '../../model/models';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';

import { convertDateToLocalDate } from '../../helpers/date-mapping';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus,
    mapPayableStatusToBuyerApprovalStatus
} from '../../helpers/status-mapping';
import { multiplyMoney } from '../../helpers/money-operations';
import { deDuplicatePendingCreditMemos } from '../../helpers/credit-memo-mapping';
import { isNil, omitBy } from 'lodash';

export class ListGroupedInvoiceSummaryForSellerRequest2 {
    public refSellerKey: string;
    public refInvoiceKey: string;
    public pagination: Pagination;
    public requestDate: DateModel;
    public useScheduledDate: boolean;
    public includePendingPayables: boolean;

    public constructor(init?: Partial<ListGroupedInvoiceSummaryForSellerRequest2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}
export class ListGroupedInvoiceSummaryForSellerResponse2 {
    public ok = false;
    public rows: InvoiceSummary2[] = [];
    public error: Error = null;
    public pagination = new Pagination();
    public totalCount = 0;
    public buyerPaidEarlyPaymentFeePercentageVisible = false;
    public buyerPaidEarlyPaymentFeePercentage = 0;

    public constructor(init?: Partial<ListGroupedInvoiceSummaryForSellerResponse2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

@Injectable({ providedIn: 'root' })
export class ListGroupedInvoiceSummaryForSellerService extends ProtoApiService<
    ListGroupedInvoiceSummaryForSellerRequest2,
    ListGroupedInvoiceSummaryForSellerResponse2
> {
    public requestType = ListGroupedInvoiceSummaryForSellerRequest2;
    public responseType = ListGroupedInvoiceSummaryForSellerResponse2;
    public endpointName = 'ListGroupedInvoiceSummaryForSeller';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService,
        private _userService: UserService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'ListGroupedInvoiceSummaryForSeller';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListGroupedInvoiceSummaryForSellerRequest2
    ): { query: string } | ListGroupedInvoiceSummaryForSellerRequest2 {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection
            } = req.pagination;
            const supplierIds = [
                this._claimService.isOnSync(DashboardFinancingSellerClaim.FinancingVendorPortal)
                    ? this._userService.getAlternateCompanyKey()
                    : req.refSellerKey
            ];
            const input: PayablesInput = omitBy(
                {
                    companyFilter: { supplierIds },
                    payableIds: [+req.refInvoiceKey],
                    pagination: {
                        skip,
                        take,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] || SortDirection.ASCENDING
                        ),
                        secondarySortColumn: new EnumType(
                            mapBuyerPayableSortColumn(secondarySortColumn)
                        ),
                        secondarySortDirection: new EnumType(
                            SortDirection[secondarySortDirection] || SortDirection.ASCENDING
                        )
                    },
                    includePendingPayables: req.includePendingPayables
                },
                isNil
            );
            const amount = { cents: true, currencyCode: true };

            return {
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: { input },
                        total: { payableCount: true },
                        rows: {
                            id: true,
                            name: true,
                            fundingDate: true,
                            dueInDays: true,
                            buyerDiscountFeeRate: true,
                            status: true,
                            statusLegacy: true,
                            supplierDiscountFeeRate: true,
                            relationship: {
                                buyer: { id: true, company: { name: true } },
                                supplier: { company: { name: true } }
                            },
                            approvals: {
                                id: true,
                                originalDueDate: true,
                                effectiveFundingDate: true,
                                amount,
                                supplierDiscountFee: {
                                    cents: true,
                                    currencyCode: true
                                },
                                invoice: {
                                    date: true,
                                    invoiceNumber: true
                                }
                            },
                            creditMemos: {
                                id: true,
                                date: true,
                                creditMemoNumber: true,
                                amount,
                                allocatedAmount: amount
                            },
                            transfers: { transferType: true }
                        }
                    }
                } as Query)}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | ListGroupedInvoiceSummaryForSellerResponse2
    ): ListGroupedInvoiceSummaryForSellerResponse2 {
        if (this._isPayLaterEnabled) {
            const { rows, total } = (res as GraphQLResponse<PayablesResponse>).data.payables;

            if (!rows.length) {
                return new ListGroupedInvoiceSummaryForSellerResponse2({
                    rows: [],
                    ok: true
                });
            }

            const payable = rows[0];
            return new ListGroupedInvoiceSummaryForSellerResponse2({
                ok: true,
                buyerPaidEarlyPaymentFeePercentageVisible:
                    payable.relationship?.scfConfig?.isBuyerPaidEarlyPaymentFeePercentageVisible ||
                    false,
                buyerPaidEarlyPaymentFeePercentage:
                    payable.relationship?.scfConfig?.buyerPaidEarlyPaymentFeePercentage || 0,
                totalCount: total.payableCount,

                rows: [...this._mapCreditMemos(payable), ...this._mapApprovals(payable)]
            });
        }

        return res as ListGroupedInvoiceSummaryForSellerResponse2;
    }

    private _mapCreditMemos(payable: Payable) {
        const { buyer } = payable.relationship;

        let deduplicatedCreditMemos = payable.creditMemos;

        if (payable.approvals.length == 0 && payable.creditMemos.length > 0) {
            deduplicatedCreditMemos = deDuplicatePendingCreditMemos(payable.creditMemos);
        }

        return (
            deduplicatedCreditMemos?.map((cm) => {
                const invoiceDate = convertDateToLocalDate(cm.date);
                const invoiceDateNew =
                    invoiceDate &&
                    new DateModel({
                        day: invoiceDate.getDate(),
                        month: invoiceDate.getMonth() + 1,
                        year: invoiceDate.getFullYear()
                    });

                let creditMemoAmount: Money;
                if (payable.approvals.length > 0) {
                    if (cm.allocatedAmount.cents == 0) {
                        creditMemoAmount = cm.amount;
                    } else {
                        creditMemoAmount = cm.allocatedAmount;
                    }
                } else {
                    // Detect pending payable and sum up the remaining amount
                    creditMemoAmount = cm.amount;
                }

                if (creditMemoAmount.cents > 0) {
                    creditMemoAmount = multiplyMoney(creditMemoAmount, -1);
                }

                return new InvoiceSummary2({
                    invoiceDate: invoiceDate,
                    invoiceDateNew: invoiceDateNew,
                    refInvoiceKey: payable.id.toString(),
                    refGroupedInvoiceKey: cm.id.toString(),
                    buyerName: buyer.company.name,
                    invoiceNumber: cm.creditMemoNumber,
                    feeRateHundredthOfBps: payable.supplierDiscountFeeRate
                        ? payable.supplierDiscountFeeRate * 100
                        : 0,
                    invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    buyerApprovalStatus: mapPayableStatusToBuyerApprovalStatus(payable.status),
                    invoiceAmount: creditMemoAmount,
                    finalApprovedAmount: creditMemoAmount
                });
            }) ?? []
        );
    }

    private _mapApprovals(payable: Payable): InvoiceSummary2[] {
        const { buyer, supplier } = payable.relationship;
        return (
            payable.approvals?.map((approval) => {
                const invoiceDate = convertDateToLocalDate(approval.invoice.date);
                const invoiceDateNew =
                    invoiceDate &&
                    new DateModel({
                        day: invoiceDate.getDate(),
                        month: invoiceDate.getMonth() + 1,
                        year: invoiceDate.getFullYear()
                    });

                const invoiceDueDate = convertDateToLocalDate(approval.originalDueDate);
                const invoiceDueDateNew =
                    invoiceDueDate &&
                    new DateModel({
                        day: invoiceDueDate.getDate(),
                        month: invoiceDueDate.getMonth() + 1,
                        year: invoiceDueDate.getFullYear()
                    });

                return new InvoiceSummary2({
                    invoiceDate,
                    invoiceDateNew,
                    invoiceDueDate,
                    invoiceDueDateNew,
                    refInvoiceKey: payable.id.toString(),
                    refGroupedInvoiceKey: approval.id,
                    buyerName: buyer.company.name,
                    invoiceNumber: approval.invoice.invoiceNumber,
                    dueIn: payable.dueInDays,
                    feeRateHundredthOfBps: payable.supplierDiscountFeeRate
                        ? payable.supplierDiscountFeeRate * 100
                        : 0,
                    invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                        payable.statusLegacy
                    ),
                    buyerApprovalStatus: mapPayableStatusToBuyerApprovalStatus(payable.status),
                    invoiceAmount: approval.amount,
                    finalApprovedAmount: approval.amount,
                    discountAmount: approval.supplierDiscountFee
                });
            }) ?? []
        );
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
